import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";
import { adminGetListings, adminApproveListing, adminRejectListing } from "../../../redux/actions/secondaryMarketActions";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";

const SecondaryListingList = () => {
  const dispatch = useDispatch();
  const { listings } = useSelector((state) => state.secondaryMarket);
  const [pageState, setPageState] = useState({ page: 0, pageSize: 20 });
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    dispatch(adminGetListings(pageState.page + 1, pageState.pageSize, statusFilter));
  }, [dispatch, pageState.page, pageState.pageSize, statusFilter]);

  const handleApprove = (id) => {
    Swal.fire({
      title: "Approve Listing?",
      text: "This listing will become visible to investors.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(adminApproveListing(id))
          .then(() => {
            Swal.fire("Approved!", "Listing is now active.", "success");
            dispatch(adminGetListings(pageState.page + 1, pageState.pageSize, statusFilter));
          })
          .catch(() => Swal.fire("Error", "Failed to approve listing.", "error"));
      }
    });
  };

  const handleReject = (id) => {
    Swal.fire({
      title: "Reject Listing?",
      input: "text",
      inputLabel: "Reason (optional)",
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(adminRejectListing(id, result.value || ""))
          .then(() => {
            Swal.fire("Rejected", "Listing has been rejected.", "info");
            dispatch(adminGetListings(pageState.page + 1, pageState.pageSize, statusFilter));
          })
          .catch(() => Swal.fire("Error", "Failed to reject listing.", "error"));
      }
    });
  };

  const formatCurrency = (amt) => {
    if (amt == null) return "—";
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 0 }).format(amt);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "campaign",
      headerName: "Campaign",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => params.row.Campaign?.fund_name || params.row.Campaign?.corporation_legal_name || "—",
    },
    {
      field: "seller",
      headerName: "Seller",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const s = params.row.Seller;
        return s ? `${s.firstname || ""} ${s.lastname || ""}`.trim() : "—";
      },
    },
    {
      field: "asking_price",
      headerName: "Asking Price",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => formatCurrency(params.value),
    },
    { field: "listing_type", headerName: "Type", width: 120 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <span style={{
          padding: "3px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: 600,
          background: params.value === "ACTIVE" ? "#e8f5e9" : params.value === "CANCELLED" ? "#ffebee" : "#f5f5f5",
          color: params.value === "ACTIVE" ? "#2e7d32" : params.value === "CANCELLED" ? "#c62828" : "#616161",
        }}>
          {params.value}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Link to={`/secondary-listings/${params.row.id}`}>
            <button style={{ padding: "4px 10px", background: "#1976d2", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>
              View
            </button>
          </Link>
          {params.row.status === "PENDING_REVIEW" && (
            <>
              <button onClick={() => handleApprove(params.row.id)} style={{ padding: "4px 10px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>
                Approve
              </button>
              <button onClick={() => handleReject(params.row.id)} style={{ padding: "4px 10px", background: "#c62828", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>
                Reject
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageShell>
      <PageHead
        title="Secondary market · listings"
        actions={
          <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: "4px", border: "1px solid #ccc" }}
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING_REVIEW">Pending Review</option>
              <option value="MATCHED">Matched</option>
              <option value="SETTLED">Settled</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="EXPIRED">Expired</option>
            </select>
        }
      />
      <div className="list">
        <div className="listContainer">
          <DataGrid
            rows={listings.items || []}
            columns={columns}
            loading={listings.loading}
            rowCount={listings.pagination?.total || 0}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={{ page: pageState.page, pageSize: pageState.pageSize }}
            paginationMode="server"
            onPaginationModelChange={(model) => setPageState(model)}
            autoHeight
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        </div>
      </div>
    </PageShell>
  );
};

export default SecondaryListingList;
